import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useEntry } from '../hooks/useEntry'
import { CandidateWarning } from '../components/CandidateWarning'
import { EvidenceSection, StatusBadge, TypeBadge } from '../components/EvidenceBadges'
import { ExampleBlock } from '../components/ExampleBlock'
import { SuggestionModal } from '../components/SuggestionModal'
import { VoteButtons } from '../components/VoteButtons'
import { SiteFooter } from '../components/SiteFooter'

export function EntryPage() {
  const { entryId } = useParams<{ entryId: string }>()
  const { entry, loading, error } = useEntry(entryId)
  const [modalOpen, setModalOpen]       = useState(false)
  const [modalType, setModalType]       = useState<'romanization' | undefined>(undefined)

  const openModal      = () => { setModalType(undefined);       setModalOpen(true) }
  const openRomanModal = () => { setModalType('romanization');  setModalOpen(true) }

  return (
    <div className="min-h-screen flex flex-col">
      <CandidateWarning />

      {/* Site nav */}
      <nav className="bg-tamang-700 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-white/80 hover:text-white text-sm flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Search
          </Link>
          <span className="text-white/40">·</span>
          <span className="text-white/70 text-sm font-mono">{entryId}</span>
        </div>
      </nav>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">

        {/* Loading */}
        {loading && (
          <div className="space-y-4 animate-pulse">
            <div className="h-12 w-1/3 bg-stone-200 rounded" />
            <div className="h-6 w-1/4 bg-stone-100 rounded" />
            <div className="h-4 w-1/2 bg-stone-100 rounded" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-rose-600 bg-rose-50 border border-rose-200 rounded-xl p-4">
            <strong>Could not load entry.</strong> {error}
          </div>
        )}

        {/* Entry */}
        {entry && (
          <div className="space-y-6">

            {/* ── Hero: Tamang form ─────────────────────────────── */}
            <section className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 space-y-4">

              {/* Badges row */}
              <div className="flex flex-wrap gap-2">
                <span className="badge-candidate">CANDIDATE — NOT VERIFIED</span>
                <StatusBadge status={entry.status} />
                <TypeBadge   type={entry.entry_type} />
              </div>

              {/* Tamang — the hero */}
              <div>
                <p className="deva text-5xl font-bold text-tamang-700 leading-tight">
                  {entry.tamang_form_surface}
                </p>
                {entry.tamang_form_normalized &&
                 entry.tamang_form_normalized !== entry.tamang_form_surface && (
                  <p className="deva text-lg text-tamang-600 opacity-70 mt-1">
                    Normalized: {entry.tamang_form_normalized}
                  </p>
                )}
                {entry.tamang_variant_note && (
                  <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2 border border-amber-100">
                    📝 {entry.tamang_variant_note}
                  </p>
                )}
              </div>

              {/* Romanized Tamang */}
              <div className="border-t border-stone-100 pt-4">
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider shrink-0">
                    Romanized
                  </span>
                  {entry.romanized_tamang ? (
                    <span className="text-xl font-medium text-tamang-600 tracking-wide">
                      {entry.romanized_tamang}
                    </span>
                  ) : (
                    <span className="text-sm text-stone-400 italic">
                      Not yet added —{' '}
                      <button
                        onClick={openRomanModal}
                        className="underline hover:text-tamang-600"
                      >
                        suggest one
                      </button>
                    </span>
                  )}
                </div>
              </div>

              {/* IPA */}
              {entry.ipa_tamang && (
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider shrink-0">
                    IPA
                  </span>
                  <span className="text-xl font-medium text-stone-500 tracking-wide">
                    /{entry.ipa_tamang}/
                  </span>
                </div>
              )}

              {/* Nepali */}
              <div className="flex items-baseline gap-3">
                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider shrink-0">
                  Nepali
                </span>
                <span className="deva text-2xl font-medium text-stone-700">
                  {entry.nepali_form}
                </span>
              </div>

              {/* English */}
              {entry.english_gloss_auto && (
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider shrink-0">
                    English
                  </span>
                  <span className="text-lg text-stone-500 italic">
                    {entry.english_gloss_auto}
                  </span>
                  <span className="badge-auto">auto-unverified</span>
                </div>
              )}

              {/* Frequency */}
              <p className="text-sm text-stone-400">
                Attested {entry.frequency.toLocaleString()} times in corpus
              </p>
            </section>

            {/* ── Examples ─────────────────────────────────────── */}
            {(entry.example_tamang_1 || entry.example_tamang_2 || entry.example_tamang_3) && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-stone-600 uppercase tracking-wider">
                  Corpus Examples
                </h2>
                {entry.example_tamang_1 && (
                  <ExampleBlock
                    index={1}
                    tamang={entry.example_tamang_1}
                    nepali={entry.example_nepali_1}
                    englishAuto={entry.example_english_1_auto}
                    sentenceId={entry.example_sentence_id_1}
                  />
                )}
                {entry.example_tamang_2 && (
                  <ExampleBlock
                    index={2}
                    tamang={entry.example_tamang_2}
                    nepali={entry.example_nepali_2}
                    englishAuto={entry.example_english_2_auto}
                    sentenceId={entry.example_sentence_id_2}
                  />
                )}
                {entry.example_tamang_3 && (
                  <ExampleBlock
                    index={3}
                    tamang={entry.example_tamang_3}
                    nepali={entry.example_nepali_3}
                    englishAuto={entry.example_english_3_auto}
                    sentenceId={entry.example_sentence_id_3}
                  />
                )}
              </section>
            )}

            {/* ── Evidence ─────────────────────────────────────── */}
            <EvidenceSection entry={entry} />

            {/* ── Vote on translation ──────────────────────────── */}
            <VoteButtons entryId={entry.entry_id} />

            {/* ── Suggest correction ───────────────────────────── */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center space-y-3">
              <p className="text-stone-600">
                Is this entry incorrect, incomplete, or missing a dialect variant?
              </p>
              <button
                className="btn-primary text-base px-6 py-3"
                onClick={openModal}
              >
                ✏️ Suggest a Correction
              </button>
              <p className="text-xs text-stone-400">
                Suggestions are stored separately and reviewed by the team.
                You cannot overwrite dictionary entries.
              </p>
            </div>

          </div>
        )}
      </main>

      {/* Suggestion modal */}
      {entry && (
        <SuggestionModal
          entry={entry}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          defaultType={modalType}
        />
      )}

      <SiteFooter />
    </div>
  )
}
